import { createContext, useContext } from 'react';
import type { Impersonator, Principal, RbacArea, RbacLevel } from '@catalyst-psa/shared';

export interface AuthContextValue {
  /** Resolved principal from GET /auth/me, or null while loading / unauthenticated. */
  principal: Principal | null;
  /** Role names assigned to the principal. */
  roles: string[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  /** Convenience flag for the seeded Admin role. */
  isAdmin: boolean;
  /** True when the member may approve / un-submit submitted timesheets (Time Approver). */
  isTimeApprover: boolean;
  /** True when the principal's effective permission for `area` satisfies `level` (default `view`). */
  can: (area: RbacArea, level?: RbacLevel) => boolean;
  /** True when the principal is allowed to see the sidebar menu item with the given key. */
  canSeeMenu: (key: string) => boolean;
  refetch: () => void;
  /** The real admin behind an impersonated session, or null when not impersonating. */
  impersonator: Impersonator | null;
  /** Begin impersonating the given member (admin only). */
  startImpersonation: (memberId: string) => void;
  /** Stop impersonating and return to the admin's own identity. */
  stopImpersonation: () => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an <AuthProvider>.');
  }
  return context;
}
